import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router";
import { FilterChip } from "@/components/design/FilterChip";
import { Section } from "@/components/design/Section";
import { fetchFilmography, TobisNotConfiguredError, type TobisFilmography } from "@/lib/tobis";
import { logApplicationFailure } from "@/lib/user-error";
import { useTobisFavorites } from "@/lib/use-tobis-favorites";
import { TobisDossier } from "./TobisDossier";
import { TobisFavoriteButton } from "./TobisFavoriteButton";
import { TobisPageShell } from "./TobisPageShell";
import { TobisFilmographyGraph } from "./TobisFilmographyGraph";
import { TobisAvatar } from "./TobisPersonRow";

type MediumFilter = "" | "movie" | "series";

// Personenprofil im Zuschnitt von /save/basics: Stammdaten links, Inhalt rechts.
// Die Filmografie steht an der Stelle der Berufserfahrung und rendert als
// Verlaufsgraph mit der Lane-Regel aus ADR-0023, siehe TobisFilmographyGraph.

function Fact({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[12px] text-ink/45">{label}</dt>
      <dd className="mt-0.5 text-[13px] text-ink/80">{value}</dd>
    </div>
  );
}

/**
 * TMDB-Personenseite. Kein IMDb-Pendant (siehe TobisTalent.tmdbPersonId) - im
 * Gegensatz zur Titelseite (imdbUrl/tmdbUrl in TobisTitlePage.tsx) gibt es fuer
 * Personen keine belastbare IMDb-Bruecke, ein Link dorthin waere Raten.
 */
function tmdbPersonUrl(tmdbPersonId: string | null): string | undefined {
  const id = tmdbPersonId?.replace(/^tmdb:/, "");
  return id ? `https://www.themoviedb.org/person/${id}` : undefined;
}

// Gedeckelt, sonst sprengt eine mit Nominierungen reiche Karriere (40+
// Eintraege sind bei echten Auszeichnungs-Vielspielern keine Seltenheit) die
// linke Spalte. Die Sortierung liefert schon "gewonnen vor nominiert, neuestes
// Jahr zuerst" (siehe getTalent), die Top 8 sind also die auffaelligsten.
const AWARDS_SHOWN = 8;

export function TobisTalentPage() {
  const intl = useIntl();
  const { personId } = useParams<{ personId: string }>();
  const [data, setData] = useState<TobisFilmography | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [mediumFilter, setMediumFilter] = useState<MediumFilter>("");
  const favorites = useTobisFavorites();

  const load = useCallback(async (id: string) => {
    try {
      setData(await fetchFilmography(id));
      setPhase("ready");
    } catch (err) {
      if (!(err instanceof TobisNotConfiguredError)) logApplicationFailure("tobis.filmography");
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    if (personId) void load(personId);
  }, [personId, load]);

  const person = data?.talent;
  const filmographyTitle = intl.formatMessage({
    id: "tobis.person.timeline",
    defaultMessage: "Filmography",
  });
  // Rein clientseitig: die Credits liegen mit der Antwort schon vollstaendig
  // vor, ein Server-Roundtrip fuer "Filme"/"Serien" waere fuer eine Person mit
  // hoechstens ein paar Hundert Rollen unnoetig - anders als der Medium-Filter
  // in der Personenliste (TobisTalentFilterBar), der ueber den ganzen Bestand
  // filtert.
  const filteredCredits = useMemo(
    () => (data?.credits ?? []).filter((c) => mediumFilter === "" || c.medium === mediumFilter),
    [data?.credits, mediumFilter],
  );

  // Auszeichnung -> Titelseite: award.workImdbId kommt aus einer Wikidata-
  // Qualifier auf der Auszeichnung selbst (P1686 "for work"), die Filmografie
  // liegt mit der Antwort schon vollstaendig vor - reiner Abgleich ueber die
  // IMDb-ID, kein zusaetzlicher Request.
  const tvmazeIdByImdbId = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of data?.credits ?? []) {
      if (c.imdbId && c.tvmazeId) map.set(c.imdbId, c.tvmazeId);
    }
    return map;
  }, [data?.credits]);

  return (
    <TobisPageShell
      eyebrow={intl.formatMessage({ id: "tobis.eyebrow", defaultMessage: "Movie" })}
      title={person?.name ?? ""}
      // person.personId und nicht der URL-Parameter: die Seite ist auch unter
      // der tvmaze- oder tmdb-Id erreichbar, gemerkt wird immer die
      // zusammengefuehrte Person (siehe favorites.ts im BFF).
      action={
        person ? (
          <TobisFavoriteButton
            marked={favorites.isFavorite("person", person.personId)}
            onToggle={() => favorites.toggle("person", person.personId)}
          />
        ) : undefined
      }
    >
      <div>
        <Link to="/tobis/people" className="text-[13px] text-ink/55 transition hover:text-ink">
          {intl.formatMessage({ id: "tobis.person.back", defaultMessage: "Back to people" })}
        </Link>
      </div>

      {phase === "error" ? (
        <Section title={filmographyTitle}>
          <p className="text-sm text-ink/55">
            {intl.formatMessage({
              id: "tobis.loadError",
              defaultMessage: "The overview could not be loaded.",
            })}
          </p>
        </Section>
      ) : null}

      {person ? (
        <div className="grid grid-cols-1 items-start gap-6 @lg:grid-cols-[minmax(280px,0.5fr)_minmax(0,1fr)]">
          {/* Stammdaten, wie die linke Spalte des Profils */}
          <div className="box bg-surface p-6">
            <div className="flex items-center gap-4">
              <TobisAvatar name={person.name} photoUrl={person.photoUrl} />
              <div className="min-w-0">
                <div className="truncate font-serif text-xl leading-tight">{person.name}</div>
                <div className="mt-0.5 text-[12px] text-ink/40">
                  {intl.formatMessage(
                    { id: "tobis.people.credits", defaultMessage: "{count} roles" },
                    { count: person.creditCount },
                  )}
                </div>
                {tmdbPersonUrl(person.tmdbPersonId) ? (
                  <a
                    href={tmdbPersonUrl(person.tmdbPersonId)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-[12px] text-ink/45 transition hover:text-accent"
                  >
                    {intl.formatMessage({ id: "tobis.person.viewOnTmdb", defaultMessage: "View on TMDB" })}{" "}
                    <span aria-hidden="true">↗</span>
                  </a>
                ) : null}
              </div>
            </div>

            {/* Hochrisiko-Muster der unique_name-Regel (V0050/V0100): kein
                Jahresueberlapp der beiden Filmografien UND duenne Beleglage
                auf beiden Seiten - siehe person_alias-Migrationskommentar.
                Glyph+Text statt Farbe (wie KpiBadge-Konvention): destruktives
                Rot ist laut UI_CONVENTIONS Aktionen vorbehalten, nicht
                Datenzustaenden. */}
            {person.confidence === "low" ? (
              <div className="mt-4 flex items-start gap-2 rounded-md bg-ink/[0.04] px-3 py-2 ring-1 ring-ink/15">
                <span aria-hidden="true" className="mt-0.5 text-[13px] text-ink/45">
                  △
                </span>
                <p className="text-[12px] text-ink/70">
                  {intl.formatMessage({
                    id: "tobis.person.lowConfidence",
                    defaultMessage:
                      "This merge is uncertain: the combined filmography may belong to two different people with the same name.",
                  })}
                </p>
              </div>
            ) : null}

            <dl className="mt-6 space-y-4">
              <Fact
                label={intl.formatMessage({ id: "tobis.person.birthday", defaultMessage: "Born" })}
                value={person.birthday}
              />
              <Fact
                label={intl.formatMessage({ id: "tobis.person.country", defaultMessage: "Country" })}
                value={person.country}
              />
              <Fact
                label={intl.formatMessage({
                  id: "tobis.person.titleCount",
                  defaultMessage: "Titles",
                })}
                value={String(data?.credits.length ?? 0)}
              />
              {data && data.boxOffice.creditsWithAdmissions > 0 ? (
                <Fact
                  label={intl.formatMessage({
                    id: "tobis.person.boxOffice",
                    defaultMessage: "Box office track record",
                  })}
                  value={intl.formatMessage(
                    {
                      id: "tobis.person.boxOfficeValue",
                      defaultMessage: "{admissions} admissions (DE), from {withData} of {total} titles",
                    },
                    {
                      admissions: intl.formatNumber(Number(data.boxOffice.totalAdmissionsDe)),
                      withData: data.boxOffice.creditsWithAdmissions,
                      total: data.boxOffice.totalCredits,
                    },
                  )}
                />
              ) : null}
            </dl>

            {person.awards.length > 0 ? (
              <div className="mt-6">
                <div className="text-[12px] text-ink/45">
                  {intl.formatMessage({ id: "tobis.person.awards", defaultMessage: "Awards" })}
                </div>
                <ul className="mt-1 space-y-1">
                  {person.awards.slice(0, AWARDS_SHOWN).map((a, i) => {
                    const tvmazeId = a.workImdbId ? tvmazeIdByImdbId.get(a.workImdbId) : undefined;
                    return (
                      <li key={`${a.award}-${i}`} className="text-[13px] text-ink/80">
                        <span className="text-ink/45">
                          {a.won
                            ? intl.formatMessage({ id: "tobis.person.awardWon", defaultMessage: "Won" })
                            : intl.formatMessage({
                                id: "tobis.person.awardNominated",
                                defaultMessage: "Nominated",
                              })}
                        </span>{" "}
                        {tvmazeId ? (
                          <Link to={`/tobis/titles/${tvmazeId}`} className="hover:text-accent hover:underline">
                            {a.award}
                          </Link>
                        ) : (
                          a.award
                        )}
                        {a.year ? <span className="text-ink/45"> · {a.year}</span> : null}
                      </li>
                    );
                  })}
                </ul>
                {person.awards.length > AWARDS_SHOWN ? (
                  <div className="mt-1 text-[12px] text-ink/45">
                    {intl.formatMessage(
                      { id: "tobis.person.awardsMore", defaultMessage: "and {count} more" },
                      { count: person.awards.length - AWARDS_SHOWN },
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Fassungen, bei denen diese Person als Drehbuchautor gefuehrt
                ist (IMSDb/Script Slug ueber model.credit) - dieselbe
                Kennzahl wie auf der Titelseite, hier gebuendelt je Person. */}
            {(data?.scripts.length ?? 0) > 0 ? (
              <div className="mt-6">
                <div className="text-[12px] text-ink/45">
                  {intl.formatMessage({ id: "tobis.person.scripts", defaultMessage: "Screenplays" })}
                </div>
                <ul className="mt-1 space-y-1">
                  {data?.scripts.map((s) => (
                    <li key={s.imdbId} className="text-[13px] text-ink/80">
                      {s.title}
                      {s.year ? <span className="text-ink/45"> · {s.year}</span> : null}
                      {s.scenes !== null ? (
                        <span className="text-ink/45">
                          {" "}
                          ·{" "}
                          {intl.formatMessage(
                            { id: "tobis.person.scriptScenes", defaultMessage: "{count} scenes" },
                            { count: s.scenes },
                          )}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          {/* Filmografie an der Stelle der Berufserfahrung */}
          <Section
            title={filmographyTitle}
            right={<span className="text-[12px] text-ink/40">{filteredCredits.length}</span>}
            empty={phase === "ready" && (data?.credits.length ?? 0) === 0}
            emptyLabel={intl.formatMessage({
              id: "tobis.person.timelineEmpty",
              defaultMessage: "No roles recorded yet.",
            })}
          >
            {(data?.credits.length ?? 0) > 0 ? (
              <div className="mb-4 flex gap-1.5">
                {(
                  [
                    { value: "", labelId: "tobis.filter.all", fallback: "All" },
                    { value: "movie", labelId: "tobis.filter.movies", fallback: "Films" },
                    { value: "series", labelId: "tobis.filter.series", fallback: "Series" },
                  ] as const
                ).map((m) => (
                  <FilterChip
                    key={m.value || "all"}
                    active={mediumFilter === m.value}
                    onClick={() => setMediumFilter(m.value)}
                  >
                    {intl.formatMessage({ id: m.labelId, defaultMessage: m.fallback })}
                  </FilterChip>
                ))}
              </div>
            ) : null}
            {filteredCredits.length === 0 && (data?.credits.length ?? 0) > 0 ? (
              <p className="text-sm text-ink/55">
                {intl.formatMessage({
                  id: "tobis.person.timelineFilterEmpty",
                  defaultMessage: "No roles found for this type.",
                })}
              </p>
            ) : (
              <TobisFilmographyGraph credits={filteredCredits} />
            )}
          </Section>

          {/* Live von TMDB nachgefragt (tmdb-person-credits.ts), nicht Teil von
              model.credit: unsere eigene Discovery findet nur Titel mit
              Erscheinungsdatum im abgesuchten Fenster, TMDBs Personen-Endpunkt
              kennt auch undatierte/gerade angekuendigte Projekte. */}
          {data && data.liveCredits.length > 0 ? (
            <Section
              title={intl.formatMessage({
                id: "tobis.person.liveCreditsHeading",
                defaultMessage: "Also listed at TMDB",
              })}
              right={<span className="text-[12px] text-ink/40">{data.liveCredits.length}</span>}
            >
              <p className="mb-4 text-[12px] text-ink/45">
                {intl.formatMessage({
                  id: "tobis.person.liveCreditsHint",
                  defaultMessage:
                    "Not yet in our own catalog - fetched live from TMDB, not limited by release date.",
                })}
              </p>
              <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-3">
                {data.liveCredits.map((c) => (
                  <div key={c.tmdbId} className="flex gap-3">
                    <div className="h-16 w-11 shrink-0 overflow-hidden rounded bg-ink/[0.06]">
                      {c.posterUrl ? (
                        <img src={c.posterUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                      ) : null}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12px] text-ink/40 tabular-nums">{c.year ?? "—"}</div>
                      <div className="truncate text-[14px] text-ink">{c.titleName}</div>
                      {c.characterName ? (
                        <div className="mt-0.5 truncate text-[13px] text-ink/55">
                          {intl.formatMessage(
                            { id: "tobis.person.asRole", defaultMessage: "as {role}" },
                            { role: c.characterName },
                          )}
                        </div>
                      ) : c.jobs.length > 0 ? (
                        <div className="mt-0.5 truncate text-[13px] text-ink/55">{c.jobs.join(", ")}</div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          ) : null}
        </div>
      ) : null}

      {person && personId ? <TobisDossier kind="talent" id={personId} /> : null}
    </TobisPageShell>
  );
}
